import { Box, Stack, Typography } from '@mui/material';
import { landing } from './landingTheme';
import { formatCount, toPersianDigits } from '@/lib/format/persian';

/**
 * The illustration beside the headline: a board, a progress chip and a task chip.
 *
 * @remarks
 * <b>Built from elements rather than shipped as a picture.</b> A rendered image would be
 * sharper, and it would also be a few hundred kilobytes on the one screen where a slow first
 * paint costs the most, it would carry text no screen reader can reach, and it would be wrong
 * the first time a column was renamed. This is the product's own vocabulary, in the product's
 * own type, at any viewport.
 *
 * The numbers are illustrative and the columns are the real workflow states. Nothing here
 * claims to be live data - a marketing page that showed a real board would be showing one
 * organization's work to everybody who visited.
 */
export function HeroVisual() {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 620,
        marginInline: 'auto',

        // The whole assembly tilts very slightly, which is what stops three stacked
        // rectangles reading as a wireframe.
        perspective: '1400px',
      }}
    >
      <Board />
      <ProgressChip />
      <TaskChip />
      <Glow />
    </Box>
  );
}

/** The floating board: a title bar and three columns of cards. */
function Board() {
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 3,
        border: 1,
        borderColor: landing.borderBright,
        backgroundColor: 'rgba(13, 20, 43, 0.92)',
        boxShadow: '0 40px 90px -30px rgba(70, 60, 190, 0.65)',
        overflow: 'hidden',
        transform: 'rotateY(-6deg) rotateX(2deg)',
      }}
    >
      {/* Window chrome. Three dots read as "an application" faster than any label. */}
      <Stack
        direction="row"
        spacing={0.75}
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: landing.border,
          backgroundColor: 'rgba(8, 13, 31, 0.9)',
        }}
      >
        {['#F87171', '#FBBF24', '#34D399'].map((colour) => (
          <Box key={colour} sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: colour }} />
        ))}

        <Typography
          sx={{ ms: 'auto', fontSize: 11, color: landing.textFaint, letterSpacing: 0.2 }}
        >
          تخته کار
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.25} sx={{ p: 1.75 }}>
        <Column title="در انتظار" tone={landing.violetSoft} cards={3} />
        <Column title="در حال انجام" tone={landing.blue} cards={4} />
        <Column title="در بازبینی" tone={landing.mint} cards={2} />
      </Stack>
    </Box>
  );
}

function Column({ title, tone, cards }: { title: string; tone: string; cards: number }) {
  return (
    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          borderRadius: 1.5,
          px: 1,
          py: 0.65,
          fontSize: 11,
          fontWeight: 700,
          color: tone,
          backgroundColor: `${tone}1F`,
          border: `1px solid ${tone}33`,
          textAlign: 'center',
        }}
      >
        {title}
      </Box>

      {Array.from({ length: cards }, (_, index) => (
        <Card key={index} tone={tone} wide={index % 2 === 0} />
      ))}
    </Stack>
  );
}

/** One task card, reduced to what a card is: an avatar, a title line and a shorter line. */
function Card({ tone, wide }: { tone: string; wide: boolean }) {
  return (
    <Stack
      spacing={0.6}
      sx={{
        borderRadius: 1.5,
        p: 1,
        backgroundColor: 'rgba(24, 34, 66, 0.85)',
        border: 1,
        borderColor: landing.border,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: `${tone}66` }} />
        <Box sx={{ height: 6, borderRadius: 3, flex: 1, backgroundColor: 'rgba(159, 176, 217, 0.35)' }} />
      </Stack>

      <Box
        sx={{
          height: 6,
          borderRadius: 3,
          width: wide ? '82%' : '58%',
          backgroundColor: 'rgba(159, 176, 217, 0.18)',
        }}
      />
    </Stack>
  );
}

/** The small analytics card that overlaps the board's top corner. */
function ProgressChip() {
  return (
    <Stack
      spacing={0.75}
      sx={{
        position: 'absolute',
        insetInlineEnd: { xs: -6, sm: -28 },
        top: '16%',
        width: 150,
        p: 1.5,
        borderRadius: 2.5,
        border: 1,
        borderColor: landing.borderBright,
        backgroundColor: 'rgba(12, 19, 42, 0.94)',
        boxShadow: '0 24px 50px -20px rgba(0, 0, 0, 0.75)',
      }}
    >
      <Typography sx={{ fontSize: 11, color: landing.textMuted }}>پیشرفت پروژه</Typography>

      <Typography sx={{ fontSize: 24, fontWeight: 800, color: landing.text, lineHeight: 1 }}>
        {toPersianDigits(83)}٪
      </Typography>

      {/* A sparkline, drawn rather than imagined: the shape is the point, not the values. */}
      <Box component="svg" viewBox="0 0 120 34" sx={{ width: '100%', height: 34 }}>
        <defs>
          <linearGradient id="spark" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={landing.cyan} stopOpacity="0.45" />
            <stop offset="100%" stopColor={landing.cyan} stopOpacity="0" />
          </linearGradient>
        </defs>

        <path
          d="M2 28 L22 22 L42 25 L62 12 L82 16 L102 6 L118 9"
          fill="none"
          stroke={landing.cyan}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M2 28 L22 22 L42 25 L62 12 L82 16 L102 6 L118 9 L118 34 L2 34 Z" fill="url(#spark)" />
      </Box>
    </Stack>
  );
}

/** The checklist card that overlaps the board's bottom corner. */
function TaskChip() {
  const rows = [
    { label: 'وظایف امروز', count: 12, done: false },
    { label: 'وظایف در حال انجام', count: 8, done: false },
    { label: 'وظایف تکمیل‌شده', count: 42, done: true },
  ];

  return (
    <Stack
      spacing={1}
      sx={{
        position: 'absolute',
        insetInlineStart: { xs: -6, sm: -30 },
        bottom: '-8%',
        width: 220,
        p: 1.5,
        borderRadius: 2.5,
        border: 1,
        borderColor: landing.borderBright,
        backgroundColor: 'rgba(12, 19, 42, 0.94)',
        boxShadow: '0 24px 50px -20px rgba(0, 0, 0, 0.75)',
      }}
    >
      {rows.map((row) => (
        <Stack key={row.label} direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.75,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: row.done ? landing.mint : landing.blue,
              color: '#05081A',
              fontSize: 11,
              fontWeight: 900,
              lineHeight: 1,
            }}
          >
            ✓
          </Box>

          <Typography sx={{ fontSize: 12, color: landing.textMuted, flex: 1 }}>{row.label}</Typography>

          <Typography sx={{ fontSize: 12, fontWeight: 700, color: landing.text }}>
            {formatCount(row.count)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/** The pooled light under the board. Pure decoration, and the only thing here that is. */
function Glow() {
  return (
    <Box
      sx={{
        position: 'absolute',
        insetInline: '12%',
        bottom: '-16%',
        height: 90,
        borderRadius: '50%',
        background: `radial-gradient(closest-side, rgba(124, 92, 255, 0.45), transparent)`,
        filter: 'blur(18px)',
        zIndex: -1,
      }}
    />
  );
}
